'use client';
import { useImagePreviewContext } from '@ui/hooks/file/ImagePreviewContext';
import { IFileMeta } from '@ui/hooks/file/types';
import {
  getMessageImageSrcSetCandidates,
  resolveMessageImageFallbackUrl,
  resolveMessageImageSrcSet,
  resolveMessageImageUrls,
} from '@ui/hooks/file/message-image-sources';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import {
  getWebMessageImageSizes,
  type MessageImageLayoutWidths,
} from '@ui/hooks/file/message-image-layout';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

interface IProps {
  images: IFileMeta[];
  loading?: 'eager' | 'lazy';
  reserveSpace?: boolean;
  responsiveVariants?: boolean;
  imageLayoutWidths?: MessageImageLayoutWidths;
}

export const MessageItemImages = ({
  images,
  loading = 'eager',
  reserveSpace = false,
  responsiveVariants = false,
  imageLayoutWidths,
}: IProps) => {
  const { setCarouselPreview } = useImagePreviewContext();

  const previewUrls = useMemo(() => resolveMessageImageUrls(images), [images]);
  const sourceCounts = useMemo(() => {
    const counts = new Map<string, number>();
    images.forEach((image) => {
      const source = image.source?.trim();
      if (source) counts.set(source, (counts.get(source) || 0) + 1);
    });
    return counts;
  }, [images]);

  const getWidthClass = (index: number) => {
    const length = Math.min(images.length, 4);
    if (length === 4) return index === 0 ? 'w-full max-h-[400px]' : 'w-1/3 max-h-[200px]';
    if (length === 3) return index === 0 ? 'w-full max-h-[400px]' : 'w-1/2 w-1/3 max-h-[200px]';
    if (length === 2) return 'w-1/2';
    return 'w-full max-h-[400px]';
  };

  const onClickImage = (event: React.MouseEvent, index: number) => {
    event.preventDefault();
    event.stopPropagation();

    const mediaItems = images.map((img) => ({
      src: img.original_source || img.source,
      isVideo: isVideo(img.mime_type as string),
      name: img.name,
    }));

    setCarouselPreview(mediaItems, index);
  };

  const isVideo = (mimeType?: string) => {
    return Boolean(mimeType?.startsWith('video/'));
  };

  return (
    <div className="flex flex-wrap">
      {images?.slice(0, 4).map((image, index) => {
        const isVideoFile = isVideo(image.mime_type as string);
        const isSingleMedia = images.length === 1;
        const isTwoMedia = images.length === 2;
        const isFirstInGrid = index === 0 && images.length > 2; // Only for 3+ items
        const hasExtraMedia = images.length > 4;
        const shouldShowOverlay = hasExtraMedia && index === 3;

        if (isVideoFile) {
          const isFourFilesGrid = images.length === 4 && index > 0;
          const isThreeFilesGrid = images.length === 3 && index > 0;

          return (
            <div
              key={index}
              className={`${getWidthClass(index)} p-1`}
              data-ignore-tap="true"
              onClick={(e) => onClickImage(e, index)}>
              <div
                className="relative rounded overflow-hidden bg-black h-full"
                style={{
                  ...(isSingleMedia && {
                    aspectRatio: '16/9',
                    maxHeight: '280px',
                    maxWidth: '500px',
                  }),
                  ...(isFirstInGrid && { aspectRatio: '16/9', maxHeight: '240px', width: '100%' }),
                  ...(isTwoMedia && { aspectRatio: '16/9', width: '100%', height: '100%' }),
                  ...(isFourFilesGrid && { width: '100%', height: '200px' }),
                  ...(isThreeFilesGrid && { width: '100%', height: '200px' }),
                }}>
                <ReactPlayer
                  url={image.source}
                  controls
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                  config={{
                    file: {
                      attributes: {
                        controlsList: 'nodownload',
                        playsInline: true,
                        preload: 'metadata',
                        ...(image.thumbnailUrl ? { poster: image.thumbnailUrl } : {}),
                      },
                    },
                  }}
                />

                {shouldShowOverlay && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60">
                    <span className="text-white text-2xl font-semibold">+{images.length - 3}</span>
                  </div>
                )}
              </div>
            </div>
          );
        }

        const hasProgress = typeof image.upload_progress === 'number';
        const progress = hasProgress ? image.upload_progress || 0 : 0;
        const totalImages = images.length || 1;
        const completedCount =
          progress > 0 ? Math.max(1, Math.round((progress / 100) * totalImages)) : 0;
        const showUploadStatus = hasProgress && progress <= 100;
        const legacySource = image.source?.trim();
        const hasAmbiguousLegacySource =
          !!legacySource && (sourceCounts.get(legacySource) || 0) > 1 && !!image.original_source;
        const srcSet =
          responsiveVariants && !hasAmbiguousLegacySource ? resolveMessageImageSrcSet(image) : '';
        const candidates = srcSet ? getMessageImageSrcSetCandidates(image) : [];
        const smallestReadyVariant = candidates[0]?.uri;
        const sizes = getWebMessageImageSizes(
          image,
          index,
          images,
          reserveSpace,
          imageLayoutWidths,
        );
        const previewUrl = smallestReadyVariant || previewUrls[index] || image.source;
        const recoverImage = (target: HTMLImageElement) => {
          const failedUrl = target.currentSrc || target.src;
          const fallback =
            image.original_source?.trim() ||
            image.source?.trim() ||
            resolveMessageImageFallbackUrl(image, failedUrl);
          if (!fallback) return;
          let fallbackUrl: string;
          try {
            fallbackUrl = new URL(fallback, target.baseURI).href;
          } catch {
            return;
          }
          if (failedUrl !== fallbackUrl) {
            target.onerror = null;
            target.srcset = '';
            target.src = fallbackUrl;
          }
        };

        const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
          recoverImage(event.currentTarget);
        };
        const repairHydratedImage = (target: HTMLImageElement | null) => {
          if (target?.complete && target.naturalWidth === 0 && target.currentSrc)
            recoverImage(target);
        };

        return (
          <div
            key={index}
            className={`${getWidthClass(index)} relative p-1 ${
              reserveSpace ? 'aspect-[4/3] overflow-hidden' : ''
            }`}
            onClick={(e) => onClickImage(e, index)}
            data-ignore-tap="true">
            <div className="absolute inset-0 hidden max-h-[400px] w-full overflow-hidden sm:block">
              <img
                ref={srcSet ? repairHydratedImage : undefined}
                loading={typeof image.upload_progress === 'number' ? 'eager' : loading}
                {...(srcSet ? { sizes, srcSet } : {})}
                src={previewUrl}
                alt=""
                aria-hidden="true"
                className=" h-full w-full object-cover blur-md opacity-20"
                decoding="async"
                onError={handleImageError}
              />
            </div>

            <img
              ref={srcSet ? repairHydratedImage : undefined}
              loading={typeof image.upload_progress === 'number' ? 'eager' : loading}
              {...(srcSet ? { sizes, srcSet } : {})}
              src={previewUrl}
              alt={image.name}
              className={`h-full w-full cursor-pointer object-contain relative z-10`}
              decoding="async"
              onError={handleImageError}
            />

            {/* Upload progress overlay for optimistic DM images */}
            {showUploadStatus && (
              <div className="absolute inset-0 z-30 flex items-center justify-center rounded bg-black/50">
                <div className="flex flex-col items-center text-white text-xs font-medium">
                  <span>
                    {completedCount > 0
                      ? `${progress}% (${completedCount}/${totalImages})`
                      : 'Uploading image...'}
                  </span>
                  <div className="mt-1 h-1 w-12 overflow-hidden rounded-full bg-white/30">
                    <div
                      className="h-full bg-white"
                      style={{ width: `${Math.max(5, progress || 0)}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {shouldShowOverlay && !showUploadStatus && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60">
                <span className="text-white text-2xl font-semibold">+{images.length - 3}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
